import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type RangeDays } from '../lib/api.js';
import { getStoredUser } from '../lib/auth.js';
import { fmt } from '../lib/format.js';
import { Icon } from '../components/Icon.js';
import { GrowthChart } from '../components/GrowthChart.js';
import { Funnel } from '../components/Funnel.js';
import { EmptyState } from '../components/EmptyState.js';
import { DashboardSkeleton } from '../components/Skeleton.js';

const RANGES: { v: RangeDays; label: string }[] = [
  { v: 30, label: "30 י'" },
  { v: 90, label: "90 י'" },
  { v: 365, label: 'שנה' },
];

/** Time-of-day greeting (Israel local time) — no more "בוקר טוב" at night. */
function greeting(): string {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return 'בוקר טוב';
  if (h >= 12 && h < 17) return 'צהריים טובים';
  if (h >= 17 && h < 21) return 'ערב טוב';
  return 'לילה טוב';
}

/** First name of the logged-in user for a personal greeting (empty in the dev stub). */
function greetName(): string {
  const u = getStoredUser();
  return u?.name ? u.name.split(' ')[0] : '';
}

export function DashboardPage(): JSX.Element {
  const [range, setRange] = useState<RangeDays>(90);
  const { data, isLoading } = useQuery({ queryKey: ['dashboard', range], queryFn: () => api.dashboard(range) });

  if (isLoading || !data) {
    return <DashboardSkeleton />;
  }

  const { audience, accounts, kpis, latestBroadcast, liveBroadcast } = data;
  const atRisk = accounts.filter((a) => a.healthState === 'risk').length;
  const livePct = liveBroadcast && liveBroadcast.total ? Math.round((liveBroadcast.sent / liveBroadcast.total) * 100) : 0;

  return (
    <div className="content">
      <div className="page-head">
        <div>
          <div className="eyebrow">בית</div>
          <h1>{greeting()}{greetName() ? `, ${greetName()}` : ''}</h1>
          <div className="sub">
            {kpis.connectedAccounts > 0
              ? `${kpis.connectedAccounts} מספרים מחוברים`
              : 'אין מספרים מחוברים עדיין'}
            {audience.delta90d > 0 ? ` · הקהל צמח ב-${fmt(audience.delta90d)} ב-90 יום` : ''}
            {liveBroadcast ? ' · שידור פעיל כרגע' : ''}
          </div>
        </div>
        <Link className="btn" to="/composer" style={{ padding: '12px 22px', fontSize: 15 }}><Icon name="send" /> שלח הודעה</Link>
      </div>

      {/* hero band — the one number that matters (audience) + live broadcast pulse */}
      <div className="hero">
        <div className="card growth hero-float">
          <div className="top">
            <div className="headline">
              <div className="eyebrow">צמיחת קהל · {range === 365 ? 'שנה' : `${range} יום`}</div>
              <div className="big"><span className="num-sheen sheen-host sheen-once">{fmt(audience.total)}</span><small> חברים ב-{audience.groupCount} קבוצות</small></div>
              <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
                <span className={`delta ${audience.delta90d >= 0 ? 'up' : 'dn'}`}>
                  <Icon name="analytics" /> ‎{audience.delta90d >= 0 ? '+' : ''}{fmt(audience.delta90d)}
                </span>
                <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>
                  בטווח · <span className="mono">{audience.deltaToday >= 0 ? '+' : ''}{fmt(audience.deltaToday)}</span> היום
                </span>
              </div>
            </div>
            <div className="range-tabs">
              {RANGES.map((r) => (
                <button key={r.v} className={range === r.v ? 'on' : ''} onClick={() => setRange(r.v)}>{r.label}</button>
              ))}
            </div>
          </div>
          <GrowthChart series={audience.series} />
        </div>

        <div className="card live">
          {liveBroadcast ? (
            <>
              <div className="status-row"><span className="live-dot" /><span className="live-lbl">שידור פעיל</span></div>
              <h3>{liveBroadcast.name}</h3>
              <div className="campaign-sub">{liveBroadcast.subtitle} · {liveBroadcast.total} קבוצות</div>
              <div className="progress"><span style={{ width: `${livePct}%` }} /></div>
              <div className="prog-meta">
                <span><b>{liveBroadcast.sent}/{liveBroadcast.total}</b> קבוצות</span>
                <span>הגיע <b>{fmt(liveBroadcast.deliveredCount)}</b></span>
              </div>
              <div style={{ flex: 1 }} />
              <div className="pace-note">
                <Icon name="clock" />
                <span><b>הגנה מחסימה פעילה.</b> נשלח בקצב מוגן (~6 שנ' בין קבוצות) ומתחלק על {kpis.connectedAccounts} מספרים תקינים.</span>
              </div>
            </>
          ) : (
            <>
              <div className="status-row"><span className="live-lbl" style={{ color: 'var(--text-dim)' }}>אין שידור פעיל</span></div>
              <div style={{ flex: 1 }} />
              <div className="pace-note"><Icon name="clock" /><span>ההגנה מחסימה ממתינה. השליחה הבאה תצא בקצב מוגן.</span></div>
            </>
          )}
        </div>
      </div>

      {/* last message results + a quiet connected-number status glance */}
      <div className="lower">
        <div className="card funnel">
          <div className="card-h" style={{ padding: 0 }}>
            <h3>תוצאות ההודעה האחרונה</h3>
            <span className="meta">{latestBroadcast ? `${latestBroadcast.groupCount} קבוצות` : '—'}</span>
          </div>
          {latestBroadcast ? <Funnel steps={latestBroadcast.funnel} /> : (
            <EmptyState
              icon="send"
              title="עדיין לא שלחת הודעה"
              sub="אחרי שתשלח את ההודעה הראשונה, תראה כאן כמה נשלח, הגיע ונקרא — בזמן אמת."
              cta={<Link className="btn" to="/composer"><Icon name="send" /> כתיבת הודעה</Link>}
            />
          )}
        </div>

        <Link to="/sessions" className="card glance">
          <div className="card-h" style={{ padding: 0 }}>
            <h3>מצב המספרים</h3>
            <span className="flag">הגנה מחסימה</span>
          </div>
          <div className="glance-num mono">{kpis.connectedAccounts}<small> / {kpis.totalAccounts} מחוברים</small></div>
          {atRisk > 0 ? (
            <span className="state risk"><span className="sd" /> {atRisk === 1 ? 'מספר אחד בסיכון' : `${atRisk} מספרים בסיכון`}</span>
          ) : kpis.connectedAccounts > 0 ? (
            <span className="state ok"><span className="sd" /> כל המספרים תקינים</span>
          ) : (
            <span className="state muted"><span className="sd" /> אין מספרים מחוברים</span>
          )}
          <div style={{ flex: 1 }} />
          <div className="glance-foot">לפרטים, שלב הרצה וקבוצות ←</div>
        </Link>
      </div>
    </div>
  );
}
